/**
 * The viewer's OTIF grade bands — CR-6269.
 *
 * The bands Admin Setup → Performance → OTIF Settings edits
 * (`OTIFGradeThreshold`), for the company whose grading applies to the viewer:
 * a customer user's home company, the switched tenant for a platform admin.
 * Each band carries the label (`display_label`), the palette-resolved colour
 * (`effective_color`) and the percentage range.
 *
 * The supplier list's OTIF quick-filter chips read these so the chip a supplier
 * lands in is the band the dashboard colours it with. Before this the chips were
 * a fixed Excellent / Good / Fair / Poor scale at 95 / 90 / 75 that no settings
 * page could change.
 *
 * Suppliers get `[]`: they trade with several customers and are graded on each
 * one's own bands. Concurrent callers share one in-flight request and nothing is
 * cached past it, so an edit on OTIF Settings shows on the next page visit.
 */
import { performanceSettingsAPI, OTIFGradeThreshold } from '../lib/api';
import { useView } from '../contexts/ViewContext';
import { useFetchData } from './useFetchData';
import { usePortalMode } from './usePortalMode';
import type { DeliveryThresholds } from '../components/dashboard/SiteKPIComponents';
import { byOtifBest } from '../lib/gradeBandRange';

/**
 * The bands in the shape the dashboard helpers (`getDeliveryBand`,
 * `getDeliveryColor`) take, so a page can colour a percentage exactly as the
 * dashboard tiles do.
 *
 * @param bands - The viewer's OTIF bands from `useOTIFBands`
 * @returns Numeric bands carrying the display label and palette colour
 */
export function toDeliveryBands(bands: OTIFGradeThreshold[]): DeliveryThresholds {
  return bands.map((b) => ({
    grade: b.grade,
    label: b.display_label || b.label || b.grade,
    min_otif_percent: parseFloat(b.min_otif_percent),
    max_otif_percent: parseFloat(b.max_otif_percent),
    color: b.effective_color || b.color,
  }));
}

const inFlight = new Map<string, Promise<OTIFGradeThreshold[]>>();

/** Bands best first — highest minimum at the top, whatever their order field (CR-8677). */
export function sortOTIFBands(bands: OTIFGradeThreshold[]): OTIFGradeThreshold[] {
  return [...bands].sort(byOtifBest);
}

function fetchBands(key: string): Promise<OTIFGradeThreshold[]> {
  const pending = inFlight.get(key);
  if (pending) return pending;
  const request = performanceSettingsAPI
    .getOTIFBands()
    .then((res) => sortOTIFBands(res.data ?? []))
    .finally(() => inFlight.delete(key));
  inFlight.set(key, request);
  return request;
}

export interface OTIFBandsResult {
  bands: OTIFGradeThreshold[];
  loading: boolean;
  error: string | null;
}

/**
 * The viewer's configured OTIF bands (see the module comment).
 *
 * @returns `{ bands, loading, error }` — `bands` is `[]` for suppliers and on error.
 */
export function useOTIFBands(): OTIFBandsResult {
  const { isSupplier, isHelix } = usePortalMode();
  const skip = isSupplier && !isHelix;
  const { viewKey } = useView();
  const { data, loading, error } = useFetchData<OTIFGradeThreshold[]>(
    () => (skip ? Promise.resolve([]) : fetchBands(viewKey)),
    { deps: [skip, viewKey] },
  );
  return { bands: data ?? [], loading, error };
}
